import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Camera,
  Music2,
  Sparkles,
  ExternalLink,
  Instagram,
  Check,
  Globe,
  Radio,
  Sliders,
  Shield,
  Loader2,
  MapPin,
  ArrowUpRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import {
  ROLE_LABELS,
  ROLE_ICON_COMPONENTS,
  ROLES,
  isArtistRole,
} from '../../lib/rolePermissions';
import {
  normalizeInstagramUsername,
  checkInstagramUsernameAvailable,
  formatInstagramUrl,
  formatSpotifyUrl,
} from '../../lib/artistWebService';
import { uploadAvatarImage, validateAvatarFile } from '../../lib/avatarService';
import logo from '../../assets/SenritsuXwordmarkwhite.png';
import SEO from '../common/SEO';
import '../../styles/Auth.css';

const AVAILABLE_ROLES = [
  ROLES.SINGER,
  ROLES.RAPPER,
  ROLES.PRODUCER,
  ROLES.VIDEO_EDITOR,
  ROLES.GRAPHIC_DESIGNER,
];

const GENRE_OPTIONS = [
  'Hip Hop',
  'Rap',
  'Pop',
  'R&B',
  'Electronic',
  'Rock',
  'Indie',
  'Lo-Fi',
  'Bollywood',
  'Classical',
  'Folk',
  'Metal',
  'Other',
];

export default function OnboardingPage() {
  const {
    user,
    profile,
    roles,
    completeOnboarding,
    completeOnboardingWithData,
    createProfile,
    loading: authLoading,
  } = useAuth();
  const navigate = useNavigate();

  // Current Step: 1 = Profile, 2 = Creator Roles, 3 = Artist Web, 4 = Review & Launch
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Profile Details
  const [fullName, setFullName] = useState('');
  const [stageName, setStageName] = useState('');
  const [city, setCity] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const fileInputRef = useRef(null);

  // Step 2: Creator Roles
  const [primaryRole, setPrimaryRole] = useState('');
  const [additionalRoles, setAdditionalRoles] = useState([]);

  // Step 3: Artist Web & Link-in-Bio Details
  const [instagram, setInstagram] = useState('');
  const [instagramStatus, setInstagramStatus] = useState({ checking: false, available: null, error: '' });
  const [genre, setGenre] = useState('');
  const [secondaryGenre, setSecondaryGenre] = useState('');
  const [artistWebAbout, setArtistWebAbout] = useState('');
  const [spotifyLink, setSpotifyLink] = useState('');
  const [appleMusicLink, setAppleMusicLink] = useState('');
  const [youtubeMusicLink, setYoutubeMusicLink] = useState('');
  const [youtube, setYoutube] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [globalError, setGlobalError] = useState('');

  // Populate initial values once user / profile are ready
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
      return;
    }

    if (!authLoading && user) {
      // If user has already completed onboarding, redirect to dashboard
      if (profile && profile.onboarding_completed) {
        navigate('/dashboard', { replace: true });
        return;
      }

      // Check for incomplete profile state
      const hasIncompleteProfile = !profile || !profile.onboarding_completed;
      if (hasIncompleteProfile) {
        const meta = user.user_metadata || {};
        setFullName((prev) => prev || profile?.name || meta.full_name || meta.name || '');
        setStageName((prev) => prev || profile?.stage_name || meta.stage_name || '');
        setCity((prev) => prev || profile?.city || meta.city || '');
        setAvatarUrl((prev) => prev || profile?.avatar_url || meta.avatar_url || meta.picture || '');

        if (!primaryRole) {
          const defaultPrimary = roles?.find((r) => r.is_primary)?.role || roles?.[0] || meta.primary_role || '';
          setPrimaryRole(defaultPrimary);
        }
        if (additionalRoles.length === 0 && Array.isArray(roles)) {
          const defaultAdd = roles.filter((r) => !r.is_primary).map((r) => r.role || r);
          setAdditionalRoles(defaultAdd);
        }

        setInstagram((prev) => prev || profile?.instagram || '');
        setGenre((prev) => prev || profile?.genre || '');
        setSecondaryGenre((prev) => prev || profile?.secondary_genre || '');
        setArtistWebAbout((prev) => prev || profile?.artist_web_about || '');
        setSpotifyLink((prev) => prev || profile?.spotify_link || '');
        setAppleMusicLink((prev) => prev || profile?.apple_music_link || '');
        setYoutubeMusicLink((prev) => prev || profile?.youtube_music_link || '');
        setYoutube((prev) => prev || profile?.youtube || '');
      }
    }
  }, [authLoading, user, profile, roles, navigate, primaryRole, additionalRoles.length]);

  // Debounced Instagram availability check
  useEffect(() => {
    const cleanHandle = normalizeInstagramUsername(instagram);
    if (!cleanHandle) {
      setInstagramStatus({ checking: false, available: null, error: '' });
      return;
    }

    let isMounted = true;
    const timer = setTimeout(async () => {
      setInstagramStatus({ checking: true, available: null, error: '' });
      try {
        const res = await checkInstagramUsernameAvailable(supabase, cleanHandle, user?.id);
        if (isMounted) {
          if (res.available) {
            setInstagramStatus({ checking: false, available: true, error: '' });
          } else {
            setInstagramStatus({
              checking: false,
              available: false,
              error: `@${cleanHandle} is already claimed by another artist.`,
            });
          }
        }
      } catch (err) {
        if (isMounted) {
          setInstagramStatus({ checking: false, available: true, error: '' });
        }
      }
    }, 450);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [instagram, user?.id]);

  // Avatar file upload handler
  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError('');
    const validation = validateAvatarFile(file);
    if (!validation.valid) {
      setAvatarError(validation.error);
      return;
    }

    setAvatarUploading(true);
    try {
      const res = await uploadAvatarImage(file);
      if (res?.avatarUrl) {
        setAvatarUrl(res.avatarUrl);
      }
    } catch (err) {
      setAvatarError(err.message || 'Image upload failed. Please try again.');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Step 1 Validation & Next
  const handleStep1Next = (e) => {
    e.preventDefault();
    setGlobalError('');
    if (!stageName.trim()) {
      setGlobalError('Stage Name is required to establish your artist identity.');
      return;
    }
    setCurrentStep(2);
  };

  // Step 2 Validation & Next
  const handleStep2Next = (e) => {
    e.preventDefault();
    setGlobalError('');
    if (!primaryRole) {
      setGlobalError('Please select your primary profession to continue.');
      return;
    }
    setCurrentStep(3);
  };

  // Step 3 Validation & Next
  const handleStep3Next = (e) => {
    e.preventDefault();
    setGlobalError('');
    if (instagramStatus.available === false) {
      setGlobalError('Please choose an available Instagram handle before proceeding.');
      return;
    }
    if (
      genre &&
      secondaryGenre &&
      genre.trim().toLowerCase() === secondaryGenre.trim().toLowerCase()
    ) {
      setGlobalError('Secondary genre cannot be identical to your primary genre.');
      return;
    }
    setCurrentStep(4);
  };

  // Step 4 Final Complete & Launch
  const handleCompleteAndLaunch = async () => {
    setGlobalError('');
    setSubmitting(true);

    try {
      const payload = {
        fullName: fullName.trim() || stageName.trim(),
        stageName: stageName.trim(),
        city: city.trim() || null,
        avatarUrl: avatarUrl || null,
        primaryRole,
        additionalRoles,
        instagram: normalizeInstagramUsername(instagram) || null,
        genre: genre.trim() || null,
        secondaryGenre: secondaryGenre.trim() || null,
        artistWebAbout: artistWebAbout.trim().slice(0, 150) || null,
        spotifyLink: spotifyLink.trim() || null,
        appleMusicLink: appleMusicLink.trim() || null,
        youtubeMusicLink: youtubeMusicLink.trim() || null,
        youtube: youtube.trim() || null,
        onboarding_completed: true,
      };

      if (typeof completeOnboardingWithData === 'function') {
        await completeOnboardingWithData(payload);
      } else if (typeof createProfile === 'function') {
        await createProfile(user, payload);
        if (typeof completeOnboarding === 'function') {
          await completeOnboarding();
        }
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1200);
    } catch (err) {
      console.error('[OnboardingPage] Completion error:', err);
      setGlobalError(err.message || 'Failed to complete onboarding. Please try again.');
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="auth-skeleton">
        <div className="auth-skeleton-card">
          <div className="auth-skeleton-line medium" />
          <div className="auth-skeleton-line short" />
          <div className="auth-skeleton-line tall" />
        </div>
      </div>
    );
  }

  const normalizedCurrentIg = normalizeInstagramUsername(instagram);
  const artistUrlPreview = normalizedCurrentIg
    ? `https://senritsux.in/artist/${normalizedCurrentIg}`
    : `https://senritsux.in/artist/${normalizeInstagramUsername(stageName) || 'your-handle'}`;

  return (
    <div className="onboarding-page">
      <SEO
        title="Creator Onboarding | SenritsuX"
        description="Set up your verified creator profile, professions, and link-in-bio presence on SenritsuX."
        noindex={true}
      />
      <div className="onboarding-card onboarding-flow-card">
        {/* Brand Header */}
        <div className="onboarding-header">
          <img src={logo} alt="SenritsuX" className="auth-brand-logo" />
          <h1 className="onboarding-flow-title">Creator Onboarding</h1>
          <p className="onboarding-flow-subtitle">
            Set up your profile, professions, and link-in-bio public presence in minutes.
          </p>
        </div>

        {/* 4-Step Stepper Progress Bar */}
        <div className="onboarding-stepper" role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={4}>
          {[
            { step: 1, label: 'Profile' },
            { step: 2, label: 'Roles' },
            { step: 3, label: 'Artist Web' },
            { step: 4, label: 'Launch' },
          ].map(({ step, label }) => {
            const isCompleted = currentStep > step || submitSuccess;
            const isActive = currentStep === step;

            return (
              <div key={step} className={`stepper-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                <div className="stepper-circle">
                  {isCompleted ? <Check size={14} strokeWidth={3} /> : step}
                </div>
                <span className="stepper-label">{label}</span>
              </div>
            );
          })}
        </div>

        {/* Global Error Banner */}
        {globalError && (
          <div className="auth-global-error" style={{ marginBottom: '1.2rem' }}>
            <AlertTriangle size={16} />
            <span>{globalError}</span>
          </div>
        )}

        {/* STEP 1: Profile Identity */}
        {currentStep === 1 && (
          <form onSubmit={handleStep1Next} noValidate className="onboarding-step-form">
            <h2 className="onboarding-step-heading">Step 1: Your Identity</h2>
            <p className="onboarding-step-desc">
              How do you want listeners, collaborators, and the community to recognize you?
            </p>

            {/* Avatar Upload */}
            <div className="onboarding-avatar-section">
              <div className="onboarding-avatar-preview-box">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar Preview" className="onboarding-avatar-img" />
                ) : (
                  <div className="onboarding-avatar-placeholder">
                    {(stageName[0] || fullName[0] || '?').toUpperCase()}
                  </div>
                )}
                {avatarUploading && (
                  <div className="onboarding-avatar-loading-overlay">
                    <Loader2 size={24} className="auth-spin" />
                  </div>
                )}
              </div>

              <div className="onboarding-avatar-controls">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarSelect}
                />
                <button
                  type="button"
                  className="btn-secondary-glass onboarding-avatar-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                >
                  <Camera size={14} />
                  <span>{avatarUrl ? 'Change Photo' : 'Upload Photo'}</span>
                </button>
                <span className="onboarding-avatar-hint">JPG, PNG, or WEBP. Max 5 MB.</span>
                {avatarError && <span className="auth-field-error">{avatarError}</span>}
              </div>
            </div>

            {/* Stage Name (Required) */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-stagename">
                Stage Name / Artist Moniker <span className="auth-required">*</span>
              </label>
              <input
                id="onboard-stagename"
                type="text"
                className="auth-input"
                placeholder="e.g. MC Nova, Beat Prodigy, Sj"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                required
                autoFocus
              />
              <span className="auth-field-hint">This will be your primary artist name across SenritsuX.</span>
            </div>

            {/* Full / Legal Name */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-fullname">
                Full / Legal Name
              </label>
              <input
                id="onboard-fullname"
                type="text"
                className="auth-input"
                placeholder="e.g. Vaibhav Jha, Alex Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <span className="auth-field-hint">Used for verification, payouts, and copyright metadata.</span>
            </div>

            {/* City / Location */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-city">
                City / Base
              </label>
              <input
                id="onboard-city"
                type="text"
                className="auth-input"
                placeholder="e.g. Mumbai, Ayodhya, New Delhi, Bengaluru"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <button type="submit" className="auth-btn" style={{ marginTop: '1.5rem' }}>
              <span>Continue to Creator Roles</span>
              <ArrowRight size={16} />
            </button>
          </form>
        )}

        {/* STEP 2: Creator Roles */}
        {currentStep === 2 && (
          <form onSubmit={handleStep2Next} noValidate className="onboarding-step-form">
            <h2 className="onboarding-step-heading">Step 2: What Kind of Creator Are You?</h2>
            <p className="onboarding-step-desc">
              Select all roles that apply. Your primary role defines your main platform identity.
            </p>

            <div className="onboarding-roles-grid">
              {AVAILABLE_ROLES.map((role) => {
                const isPrimary = primaryRole === role;
                const isAdditional = additionalRoles.includes(role);
                const isSelected = isPrimary || isAdditional;
                const RoleIconComponent = ROLE_ICON_COMPONENTS[role] || User;

                return (
                  <div
                    key={role}
                    className={`onboarding-role-card ${isSelected ? 'selected' : ''} ${isPrimary ? 'primary' : ''}`}
                    onClick={() => {
                      if (!primaryRole) {
                        setPrimaryRole(role);
                      } else if (isPrimary) {
                        // If clicking the current primary, swap with first additional if available
                        if (additionalRoles.length > 0) {
                          setPrimaryRole(additionalRoles[0]);
                          setAdditionalRoles(additionalRoles.filter((r) => r !== additionalRoles[0]));
                        }
                      } else if (isAdditional) {
                        setAdditionalRoles(additionalRoles.filter((r) => r !== role));
                      } else {
                        setAdditionalRoles([...additionalRoles, role]);
                      }
                    }}
                  >
                    <div className="onboarding-role-card-top">
                      <div className="onboarding-role-icon-box">
                        <RoleIconComponent size={22} color={isSelected ? '#d9fb06' : '#ffffff'} />
                      </div>
                      <div className="onboarding-role-card-badges">
                        {isPrimary && <span className="role-primary-tag">Primary</span>}
                        {isAdditional && <span className="role-added-tag">Added</span>}
                      </div>
                    </div>

                    <h3 className="onboarding-role-name">{ROLE_LABELS[role]}</h3>

                    {isSelected && !isPrimary && (
                      <button
                        type="button"
                        className="role-make-primary-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const oldPrimary = primaryRole;
                          setPrimaryRole(role);
                          setAdditionalRoles((prev) => [
                            ...prev.filter((r) => r !== role),
                            ...(oldPrimary ? [oldPrimary] : []),
                          ]);
                        }}
                      >
                        Set as Primary
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="onboarding-btn-row" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="btn-secondary-glass"
                onClick={() => setCurrentStep(1)}
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <button
                type="submit"
                className="auth-btn"
                disabled={!primaryRole}
              >
                <span>Continue to Artist Web</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: Artist Web & Link-in-Bio Setup */}
        {currentStep === 3 && (
          <form onSubmit={handleStep3Next} noValidate className="onboarding-step-form">
            <h2 className="onboarding-step-heading">Step 3: Setup Your Artist Web</h2>
            <p className="onboarding-step-desc">
              Your Artist Web is your public link-in-bio at <span className="brand-accent">senritsux.in/artist/...</span> with all your music, streaming links, and discovery info in one place.
            </p>

            {/* Instagram Handle & Live Preview */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-instagram">
                Instagram Handle
              </label>
              <div className="onboarding-handle-input-container">
                <span className="onboarding-handle-prefix">@</span>
                <input
                  id="onboard-instagram"
                  type="text"
                  className="auth-input onboarding-handle-input"
                  placeholder="e.g. sjsamjha"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
                {instagramStatus.checking && (
                  <span className="onboarding-handle-status checking">
                    <Loader2 size={14} className="auth-spin" /> Checking
                  </span>
                )}
                {!instagramStatus.checking && instagramStatus.available === true && (
                  <span className="onboarding-handle-status available">
                    <Check size={14} /> Available
                  </span>
                )}
                {!instagramStatus.checking && instagramStatus.available === false && (
                  <span className="onboarding-handle-status taken">
                    <AlertTriangle size={14} /> Taken
                  </span>
                )}
              </div>
              {instagramStatus.error && (
                <span className="auth-field-error" style={{ display: 'block', marginTop: '0.4rem' }}>
                  {instagramStatus.error}
                </span>
              )}

              <div className="onboarding-live-url-card">
                <Globe size={14} color="#d9fb06" />
                <span className="onboarding-live-url-label">Your Public Link-in-Bio URL:</span>
                <code className="onboarding-live-url-text">{artistUrlPreview}</code>
              </div>
            </div>

            {/* Primary Genre */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-genre">
                Primary Musical Genre
              </label>
              <select
                id="onboard-genre"
                className="auth-input"
                value={genre}
                onChange={(e) => {
                  setGenre(e.target.value);
                  if (e.target.value && secondaryGenre.toLowerCase() === e.target.value.toLowerCase()) {
                    setSecondaryGenre('');
                  }
                }}
                style={{ appearance: 'auto', background: 'rgba(255,255,255,0.08)', color: '#fff' }}
              >
                <option value="" style={{ background: '#1d1933', color: '#fff' }}>
                  Select Primary Genre...
                </option>
                {GENRE_OPTIONS.map((g) => (
                  <option key={g} value={g} style={{ background: '#1d1933', color: '#fff' }}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            {/* Secondary Genre (Optional) */}
            <div className="auth-form-group">
              <label className="auth-label" htmlFor="onboard-secondary-genre">
                Secondary Musical Genre <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', fontWeight: 'normal' }}>(Optional)</span>
              </label>
              <select
                id="onboard-secondary-genre"
                className="auth-input"
                value={secondaryGenre}
                onChange={(e) => setSecondaryGenre(e.target.value)}
                style={{ appearance: 'auto', background: 'rgba(255,255,255,0.08)', color: '#fff' }}
              >
                <option value="" style={{ background: '#1d1933', color: '#fff' }}>
                  None / Optional
                </option>
                {GENRE_OPTIONS.filter((g) => !genre || g.toLowerCase() !== genre.toLowerCase()).map((g) => (
                  <option key={g} value={g} style={{ background: '#1d1933', color: '#fff' }}>
                    {g}
                  </option>
                ))}
              </select>
            </div>

            {/* Artist Web About Description (150 char limit) */}
            <div className="auth-form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label className="auth-label" htmlFor="onboard-about">
                  Artist Web About <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)' }}>(Max 150 chars)</span>
                </label>
                <span
                  className={`onboarding-char-counter ${artistWebAbout.length >= 145 ? 'warning' : ''}`}
                >
                  {artistWebAbout.length} / 150
                </span>
              </div>
              <textarea
                id="onboard-about"
                className="auth-input onboarding-textarea"
                rows={3}
                maxLength={150}
                placeholder="Punchy one-or-two line bio for your link-in-bio profile..."
                value={artistWebAbout}
                onChange={(e) => setArtistWebAbout(e.target.value.slice(0, 150))}
              />
            </div>

            {/* Streaming Links (Collapsible Section) */}
            <div className="onboarding-links-section">
              <span className="onboarding-links-title">
                <Music2 size={15} color="#d9fb06" />
                <span>DSP & Streaming Profiles (Optional)</span>
              </span>

              <div className="auth-form-group" style={{ marginTop: '0.8rem' }}>
                <input
                  type="url"
                  className="auth-input"
                  placeholder="Spotify Artist Link / URI"
                  value={spotifyLink}
                  onChange={(e) => setSpotifyLink(e.target.value)}
                />
              </div>

              <div className="auth-form-group">
                <input
                  type="url"
                  className="auth-input"
                  placeholder="Apple Music Artist Link"
                  value={appleMusicLink}
                  onChange={(e) => setAppleMusicLink(e.target.value)}
                />
              </div>

              <div className="auth-form-group">
                <input
                  type="url"
                  className="auth-input"
                  placeholder="YouTube Music / Channel URL"
                  value={youtubeMusicLink || youtube}
                  onChange={(e) => {
                    setYoutubeMusicLink(e.target.value);
                    setYoutube(e.target.value);
                  }}
                />
              </div>
            </div>

            <div className="onboarding-btn-row" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="btn-secondary-glass"
                onClick={() => setCurrentStep(2)}
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <button
                type="submit"
                className="auth-btn"
                disabled={instagramStatus.available === false}
              >
                <span>Review & Launch</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}

        {/* STEP 4: Review & Launch */}
        {currentStep === 4 && (
          <div className="onboarding-step-form">
            <h2 className="onboarding-step-heading">Step 4: Review & Launch Your Artist Web</h2>
            <p className="onboarding-step-desc">
              Here is how your profile will look on the SenritsuX Artist Web directory.
            </p>

            {/* Live Profile Card Preview */}
            <div className="onboarding-preview-wrapper">
              <div className="auth-card onboarding-preview-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div className="onboarding-preview-avatar">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={stageName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      (stageName[0] || 'A').toUpperCase()
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>
                        {stageName || 'Your Artist Name'}
                      </span>
                      <span className="artist-card-view-symbol" aria-hidden="true">
                        <ArrowUpRight size={15} />
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      {primaryRole && (
                        <span className="onboarding-preview-badge role-tag">
                          {ROLE_LABELS[primaryRole] || primaryRole}
                        </span>
                      )}
                      {genre && (
                        <span className="onboarding-preview-badge genre-tag">
                          {genre.toUpperCase()}{secondaryGenre ? ` · ${secondaryGenre.toUpperCase()}` : ''}
                        </span>
                      )}
                      {city && (
                        <span className="onboarding-preview-badge city-tag">
                          <MapPin size={10} color="#d9fb06" /> {city}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {artistWebAbout ? (
                  <p className="onboarding-preview-about">{artistWebAbout}</p>
                ) : (
                  <p className="onboarding-preview-about placeholder">No bio provided yet.</p>
                )}

                <div className="onboarding-preview-footer">
                  <span className="onboarding-preview-link">
                    <Globe size={13} color="#d9fb06" />
                    <span>{artistUrlPreview}</span>
                  </span>
                </div>
              </div>
            </div>

            {submitSuccess && (
              <div className="onboarding-success-banner">
                <CheckCircle2 size={24} color="#d9fb06" />
                <div>
                  <h4 style={{ margin: 0, color: '#d9fb06', fontWeight: 700 }}>Profile Launched Successfully!</h4>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)' }}>
                    Redirecting you directly into your dashboard...
                  </p>
                </div>
              </div>
            )}

            <div className="onboarding-btn-row" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="btn-secondary-glass"
                onClick={() => setCurrentStep(3)}
                disabled={submitting || submitSuccess}
              >
                <ArrowLeft size={16} />
                <span>Back</span>
              </button>
              <button
                type="button"
                className="auth-btn"
                onClick={handleCompleteAndLaunch}
                disabled={submitting || submitSuccess}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="auth-spin" />
                    <span>Launching Profile...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    <span>Launch My Artist Web & Go to Dashboard</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
